"use client";

import type React from "react";
import { useEffect, useImperativeHandle, useRef } from "react";
import type { FieldValues, FormState } from "react-hook-form";
import { get, type Resolver, useForm as useHookForm } from "react-hook-form";
import { composeRefs } from "src/components/common/Composition";
import { createEvent } from "src/lib/EventUtils";
import { useForceUpdate, useUpdateEffect } from "src/lib/hooks/ClientHooks";
import { isDeepEqual } from "src/lib/MiscFrontendUtils";
import type * as zod from "zod";
import { useTranslateText } from "#i18n";
import { FormContext } from "./FormContext";

import { zodResolver } from "./ZodResolver";

export interface FieldChange {
  name: string | undefined;
  value: any;
}

export interface FormControllerHandle {
  submit: () => Promise<boolean>;
  validate: () => Promise<boolean>;
  elementRef: React.Ref<HTMLFormElement>;
}

export interface FormProps<T extends FieldValues>
  extends Omit<
    React.FormHTMLAttributes<HTMLFormElement>,
    "onSubmit" | "onChange" | "value" | "defaultValue"
  > {
  ref?: React.Ref<HTMLFormElement>;
  handleRef?: React.Ref<FormControllerHandle>;
  validate?: Resolver<T>;
  validationSchema?: zod.ZodType<T>;
  onInitialValues?: (data: FieldValues) => void;
  onSubmit?: (data: FieldValues, event: React.FormEvent<HTMLFormElement>) => void;
  defaultValue?: any;
  value?: any;
  disabled?: boolean;
  onChange?: (values: FieldValues, changed: FieldChange) => void;
  resetKey?: string | number;
}

const mergeResolverResults = (a: any, b: any) => {
  return {
    errors: {
      ...a.errors,
      ...b.errors,
    },
    values: {
      ...a.values,
      ...b.values,
    },
  };
};

const Form = <T extends FieldValues>(props: FormProps<T>) => {
  const {
    resetKey,
    children,
    ref,
    handleRef,
    value,
    disabled,
    defaultValue,
    onSubmit,
    onChange,
    validationSchema,
    validate,
    onInitialValues,
    ...otherProps
  } = props;
  const translateText = useTranslateText();
  const zodResolverInstance = validationSchema
    ? // @ts-expect-error
      zodResolver(validationSchema, undefined, { translateMessage: translateText })
    : undefined;

  const form = useHookForm({
    defaultValues: defaultValue ?? value,
    values: value,
    disabled,
    mode: "onChange",
    resolver: async (data, context, options) => {
      let result: any = {
        errors: {},
        values: {},
      };
      if (zodResolverInstance) {
        result = mergeResolverResults(result, await zodResolverInstance(data, context, options));
      }
      if (validate) {
        result = mergeResolverResults(result, await validate(data, context, options));
      }

      const formValues = { ...form.getValues(), ...result.values };
      for (const [id, field] of Object.entries(options.fields)) {
        if (typeof field.validate === "function") {
          const value = get(formValues, id);
          const error = await field.validate(value, formValues);
          if (typeof error === "string") {
            result.errors[id] = {
              type: "custom",
              message: error,
            };
          }
        }
      }

      return result;
    },
  });

  useEffect(() => {
    // fire state update when disabled changes
    form.control._subjects.state.next({ disabled: disabled });
  }, [disabled]);

  const [forceUpdate] = useForceUpdate();

  useUpdateEffect(() => {
    const newValues = defaultValue ?? value;
    previousValuesRef.current = newValues;
    form.reset(newValues);

    onInitialValues?.(form.getValues());
  }, [resetKey]);

  useEffect(() => {
    onInitialValues?.(form.getValues());
  }, []);

  const previousValuesRef = useRef<FieldValues | undefined>(form.getValues());
  previousValuesRef.current = structuredClone(form.getValues());

  const valueRef = useRef<any>(value);
  valueRef.current = value;

  const onChangeRef = useRef<any>(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const sub = form.watch((values, change) => {
      if (change.name) {
        const previousValue =
          get(valueRef.current, change.name) ??
          (previousValuesRef.current && get(previousValuesRef.current, change.name));
        const newValue = get(values, change.name);
        if (!isDeepEqual(previousValue, newValue)) {
          onChangeRef.current?.(values, {
            name: change.name,
            value: newValue,
          });
        }
      }

      if (!change.name) {
        forceUpdate();
      }
    });
    return () => {
      sub.unsubscribe();
    };
  }, []);

  const selfRef = useRef<HTMLFormElement>(null);

  useImperativeHandle(handleRef, () => ({
    submit: async () => {
      const formElement = selfRef.current;
      if (!formElement) {
        return false;
      }
      return new Promise((resolve, reject) => {
        const formSubmitEvent = createEvent("submit", formElement);
        form.handleSubmit(
          (data, event) => {
            if (onSubmit) {
              onSubmit(data as T, event as unknown as React.FormEvent<HTMLFormElement>);
            }
            resolve(true);
          },
          () => {
            resolve(false);
          },
        )(formSubmitEvent);
      });
    },
    validate: async () => {
      return await form.trigger();
    },
    elementRef: selfRef,
  }));

  // Reading a key of react-hook-form's formState proxy subscribes the form to it. Subscribe to
  // isValid (submit buttons need it) and nothing else: an isValidating subscription makes fields that
  // register during render (e.g. after a reset) synchronously update Form mid-render.
  void form.formState.isValid;
  const getFormState = () =>
    ({
      ...form.control._formState,
      defaultValues: form.control._defaultValues,
      // The raw state only records `disabled` at creation; the prop is the current value.
      disabled: Boolean(disabled),
    }) as FormState<T>;

  return (
    <FormContext.Provider value={{ ...form, getFormState, value }}>
      <form
        ref={composeRefs(selfRef, ref, (formElement) => {
          if (formElement) {
            formElement.addEventListener(
              "fieldChange",
              (event: Event) => {
                form.setValue((event as any).target.name, (event as any).target.value);
              },
              false,
            );
          }
        })}
        noValidate={true}
        onSubmit={form.handleSubmit((data, event) => {
          if (onSubmit) {
            onSubmit(form.getValues() as T, event as unknown as React.FormEvent<HTMLFormElement>);
          }
        })}
        {...otherProps}
      >
        {children}
      </form>
    </FormContext.Provider>
  );
};

const dispatchFieldChange = (form: HTMLFormElement, name: string, value: any) => {
  const event = createEvent("fieldChange", {
    name,
    value,
  });
  form.dispatchEvent(event);
};

export default Form;
